//libraries
import React, { useRef, useEffect, useState } from 'react'

// components
import Section from '/react4xp/components/Section'
import Mask from '/react4xp/components/Mask'

import Button from '/react4xp/components/Button'
import Link from '/react4xp/components/Link'
import Form from '/react4xp/components/Form/Form'
import Input from '/react4xp/components/Form/Input'
import TextArea from '/react4xp/components/Form/TextArea'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Select from '/react4xp/components/Form/Select'
import Upload from '/react4xp/components/Form/Upload'

// utils
import { isNonWhitespaceString, slugify } from '/lib/reactutils/utils.js'

// prop types
export interface ModularFormProps {
    section: {
        bgColor: 'yoghurt' | 'blue'
        paddingTop?: 'smaller' | 'small' | 'medium' | 'big' | 'none'
        paddingBottom?: 'smaller' | 'small' | 'medium' | 'big' | 'none'
    }
    title?: string
    submit: {
        label: string
        url: string
    }
    cancelLabel?: string
    fields: Array<{
        size: '1/1' | '1/2' | '1/3' | '2/3'
        type: 'text' | 'email' | 'phone' | 'textarea' | 'upload'
        label?: string
        name: string
        placeholder?: string
        isRequired?: boolean
        isDisabled?: boolean
        defaultEmail?: string

        // interface for <select>
        selectFields?: {
            firstOption: string
            options: Array<{
                label: string
            }>
        }

        // interface for <textarea>
        textAreaWordLimit?: number

        // interface for <input type='checkbox'>

        // interface for <input type='radio'>

        // interface for upload
        upload?: {
            accept: string
            multiple: boolean
            maxFileSize: number
        }
    }>
    strings?: {
        sendButton?: string
        cancelButton?: string
        validationRequired?: string
        validationInvalidEmail?: string
        validationMaxLength?: string
        validationMinLength?: string
        validationMaxFileSize?: string
        popupSuccessTitle?: string
        popupSuccessContent?: string
        popupClose?: string
        popupErrorTitle?: string
        popupErrorContent?: string
    }
}

export default function ModularForm({
    section,
    title,
    submit,
    cancelLabel,
    fields,
    strings
}: ModularFormProps) {

    const formRef = useRef(null)

    const [isImageUploaded, setIsImageUploaded] = useState('')

    // change the above state when the user uploads a photo
    // we will use this to make the checkbox below the upload field required / mandatory or not
    const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsImageUploaded(e.target.value)
    }

	useEffect(() => {
		document.addEventListener('fileDeleted', () => {
			setIsImageUploaded('')
		})
	}, [])

    return (
        <Section
            bgColor={section?.bgColor}
            paddingTop={section?.paddingTop}
            paddingBottom={section?.paddingBottom}
            className='modular-form-section'
        >
            <div className='container'>
                <div className='row'>
                    <div className='col-md-10 offset-md-1 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3'>
                        <Mask spacing='small' bgColor='white'>

                            {isNonWhitespaceString(title) && (
                                <h2 className='title-top h3'>
                                    {title}
                                </h2>  
                            )}

                            <div className='modular-form'>

                                <Form
                                    isFormData
                                    clearOnSuccess={true}
                                    ref={formRef}
                                    endpoint={submit.url}
                                    popupSuccessContent={{
                                        hasCloseButton: true,
                                        closeButtonText: strings?.popupClose || 'Lukk',
                                        title: strings?.popupSuccessTitle || 'Suksess',
                                        content: strings?.popupSuccessContent || 'Profilen din ble oppdatert.',
                                        actionButtons: {
                                            primary: {
                                                text: strings?.popupClose || 'Lukk',
                                                closeOnClick: true
                                            }
                                        }
                                    }}
                                    popupErrorContent={{
                                        hasCloseButton: true,
                                        closeButtonText: strings?.popupClose || 'Lukk',
                                        title: strings?.popupErrorTitle || 'Det oppsto en feil under forsøk på å oppdatere innstillingene dine.',
                                        content: strings?.popupErrorContent || 'Vennligst prøv igjen om noen minutter. Hvis problemet vedvarer, vennligst kontakt oss.',
                                        actionButtons: {
                                            primary: {
                                                text: strings?.popupClose || 'Lukk',
                                                closeOnClick: true
                                                //onClick: () => { alert('test') }
                                            }
                                        }
                                    }}
                                >

                                    <div className='row'>
                                        {fields.map((item, i) => {

                                            const divSize =
                                                item.size === '1/2' ? 'col-md-6' :
                                                item.size === '1/1' ? 'col-12' :
                                                item.size === '1/3' ? 'col-md-4' :
                                                'col-md-8'
                                            ;

                                            switch (item.type) {
                                                case 'text':
                                                    return (
                                                        <div key={i} className={divSize}>
                                                            <Input
                                                                name={item.name}
                                                                id={slugify(item.name)}
                                                                label={item.label}
                                                                placeholder={item.placeholder}
                                                                required={item.isRequired}
                                                                disabled={item.isDisabled}
                                                                type='text'
                                                                size='full-width'
                                                                errorMessages={{
                                                                    required: strings?.validationRequired || 'Dette feltet er obligatorisk',
                                                                }}
                                                            />
                                                        </div>
                                                    )

                                                case 'email':
                                                    return (
                                                        <div key={i} className={divSize}>
                                                            <Input
                                                                name={item.name}
                                                                id={slugify(item.name)}
                                                                label={item.label}
                                                                placeholder={item.placeholder}
                                                                required={item.isRequired}
                                                                disabled={item.isDisabled}
                                                                value={item.defaultEmail || ''}
                                                                type='email'
                                                                size='full-width'
                                                                errorMessages={{
                                                                    required: strings?.validationRequired || 'Dette feltet er obligatorisk',
                                                                    invalidEmail: strings?.validationInvalidEmail || 'Ugyldig e-postadresse'
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                
                                                case 'phone':
                                                        return (
                                                            <div key={i} className={divSize}>
                                                                <Input
                                                                    name={item.name}
                                                                    id={slugify(item.name)}
                                                                    label={item.label}
                                                                    placeholder={item.placeholder}
                                                                    required={item.isRequired}
                                                                    disabled={item.isDisabled}
                                                                    type='tel'
                                                                    size='full-width'
                                                                    errorMessages={{
                                                                        required: strings?.validationRequired || 'Dette feltet er obligatorisk'
                                                                    }}
                                                                />
                                                            </div>
                                                        )

                                                case 'textarea':
                                                    return (
                                                        <div key={i} className={divSize}>
                                                            <TextArea
                                                                name={item.name}
                                                                id={slugify(item.name)}
                                                                label={item.label}
                                                                placeholder={item.placeholder}
                                                                required={item.isRequired}
                                                                disabled={item.isDisabled}
                                                                size='full-width'
                                                                minLength={5}
                                                                maxLength={{
                                                                    limit: item.textAreaWordLimit || 300,
                                                                    text: 'tegn'
                                                                }}
                                                                errorMessages={{
                                                                    required: strings?.validationRequired || 'Dette feltet er obligatorisk',
                                                                    maxLength: strings?.validationMaxLength || 'Maksimalt antall tegn overskredet',
                                                                    minLength: strings?.validationMinLength || 'Meldingen er for kort'
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                    
                                                case 'upload':
                                                    return (
                                                        <div key={i} className='col-12'>
                                                            <Upload
                                                                id={slugify(item.name)}
                                                                label={item.label}
                                                                name={item.name}
                                                                className='upload'
                                                                accept={item.upload?.accept}
                                                                maxFileSize={item.upload?.maxFileSize}
                                                                onChange={handleUploadChange}
                                                                required={item.isRequired}
                                                                multiple={item.upload?.multiple}
                                                                errorMessages={{
                                                                    required: strings?.validationRequired || 'Dette feltet er obligatorisk',
                                                                    maxFileSize: strings?.validationMaxFileSize || 'Filstørrelsen overskrider maksimalgrensen på'
                                                                }}
                                                            />
                                                        </div>
                                                    )

                                                /*
                                                case 'select':
                                                    return (
                                                        <div key={i} className={divSize}>
                                                            <Select
                                                                name={item.name}
                                                                id={slugify(item.name)}
                                                                label={item.label}
                                                                size='full-width'
                                                                required={item.isRequired}
                                                                disabled={item.isDisabled}
                                                                defaultValue=''
                                                                errorMessages={{
                                                                    required: 'Dette feltet er obligatorisk'
                                                                }}
                                                            >
                                                                
                                                                <option value='' disabled>
                                                                    {item.selectFields?.firstOption || 'Velg'}
                                                                </option>

                                                                {item.selectFields.options.map((option, i) => (
                                                                    <option
                                                                        key={i}
                                                                        value={option.label}
                                                                    >
                                                                        {option.label}
                                                                    </option>
                                                                ))}

                                                            </Select>
                                                        </div>
                                                    )
                                                */

                                                default:
                                                    return null
                                            }

                                        })}
                                    </div>

                                    <div className='two-buttons'>

                                        <Button
                                            hasBorders
                                            hover='blue-dark'
                                            type='submit'
                                            noUnderline
                                        >
                                            {submit.label}
                                        </Button>

                                        {cancelLabel && (
                                            <Link
                                                type='button'
                                                text={cancelLabel}
                                                onClick={() => {
                                                    formRef.current?.reset()
                                                    document.dispatchEvent(new Event('resetForm'))
                                                }}
                                            />
                                        )}

                                    </div>

                                    {/*
                                    <Upload
                                        id='contact-upload'
                                        label='Last opp bilde'
                                        name='Last opp bilde'
                                        className='upload'
                                        accept='image/*, application/pdf, application/zip'
                                        multiple={true}
                                        maxFileSize={25} // this will always be mb
                                        errorMessages={{
                                            required: 'Dette feltet er obligatorisk',
                                            maxFileSize: 'Filstørrelsen overskrider maksimalgrensen på' // this message will only appear in an alert if the use uploads a file larger than 25mb
                                        }}
                                    />

                                    <Checkbox
                                        id='contact-form-terms'
                                        name='Jeg godkjenner personvilkårene'
                                        className='margin-bottom'
                                        required
                                        errorMessages={{
                                            required: 'Dette feltet er obligatorisk'
                                        }}
                                    >
                                        Jeg godkjenner <a href='#'>personvilkårene</a>
                                    </Checkbox>
                                    */}

                                </Form>

                            </div>
                        </Mask>
                    </div>
                </div>
            </div>
        </Section>
    )
}